import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationManager({ user }) {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vapidAvailable, setVapidAvailable] = useState(false);

  useEffect(() => {
    if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPermission(Notification.permission);
    checkVapidAndSubscription();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Show banner only if VAPID available, not subscribed and permission not denied
    if (vapidAvailable && permission === 'default' && !subscribed) {
      const dismissed = sessionStorage.getItem('push_banner_dismissed');
      if (!dismissed) {
        const timer = setTimeout(() => setShowBanner(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [permission, subscribed, user, vapidAvailable]);

  const checkVapidAndSubscription = useCallback(async () => {
    try {
      const vapidRes = await axios.get('/push/vapid-public-key');
      if (vapidRes.data?.public_key) {
        setVapidAvailable(true);
      }
    } catch {
      setVapidAvailable(false);
      return;
    }
    try {
      const res = await axios.get('/push/status');
      setSubscribed(res.data?.subscribed || false);
    } catch {}
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      // 1. Get VAPID key
      const vapidRes = await axios.get('/push/vapid-public-key');
      const vapidKey = vapidRes.data.public_key;

      // 2. Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // 3. Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Permission de notification refusee');
        setLoading(false);
        return;
      }

      // 4. Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      // 5. Send subscription to backend
      const subJSON = subscription.toJSON();
      await axios.post('/push/subscribe', {
        endpoint: subJSON.endpoint,
        keys: subJSON.keys,
      });

      setSubscribed(true);
      setShowBanner(false);
      toast.success('Notifications push activees !');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'VAPID non configuré') {
        toast.error('Les notifications push ne sont pas encore configurees sur ce serveur');
      } else {
        toast.error(detail || 'Erreur lors de l\'activation des notifications');
      }
    }
    setLoading(false);
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      }
      await axios.delete('/push/unsubscribe');
      setSubscribed(false);
      toast.success('Notifications push desactivees');
    } catch (err) {
      toast.error('Erreur lors de la desactivation');
    }
    setLoading(false);
  };

  const dismissBanner = () => {
    setShowBanner(false);
    sessionStorage.setItem('push_banner_dismissed', '1');
  };

  // Don't render if push is not supported
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  if (!user) return null;

  // Permission denied - nothing to show
  if (permission === 'denied') return null;

  // Floating banner for first-time users
  if (showBanner && !subscribed) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 max-w-sm bg-[#0F172A] text-white rounded-xl shadow-2xl p-4 animate-in slide-in-from-bottom-4"
        data-testid="push-notification-banner"
      >
        <button
          onClick={dismissBanner}
          className="absolute top-2 right-2 text-slate-400 hover:text-white"
          data-testid="push-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#8DC63F] flex items-center justify-center flex-shrink-0">
            <Bell className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-sm mb-1">Activez les notifications</p>
            <p className="text-xs text-slate-300 mb-3">
              Recevez une alerte instantanee pour chaque nouveau document, message ou mise a jour importante.
            </p>
            <Button
              size="sm"
              className="bg-[#8DC63F] hover:bg-[#7ab535] text-white text-xs"
              onClick={subscribe}
              disabled={loading}
              data-testid="push-banner-activate"
            >
              {loading ? 'Activation...' : 'Activer'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// Export a small toggle button for use in profile/settings
export function PushToggleButton({ user }) {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !('PushManager' in window)) return;
    checkStatus();
  }, [user]);

  const checkStatus = async () => {
    try {
      const res = await axios.get('/push/status');
      setSubscribed(res.data?.subscribed || false);
    } catch {}
  };

  const toggle = async () => {
    setLoading(true);
    if (subscribed) {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
        }
        await axios.delete('/push/unsubscribe');
        setSubscribed(false);
        toast.success('Notifications push desactivees');
      } catch {
        toast.error('Erreur');
      }
    } else {
      try {
        const vapidRes = await axios.get('/push/vapid-public-key');
        const reg = await navigator.serviceWorker.register('/sw-push.js');
        await navigator.serviceWorker.ready;
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          toast.error('Permission refusee par le navigateur');
          setLoading(false);
          return;
        }
        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidRes.data.public_key),
        });
        const subJSON = subscription.toJSON();
        await axios.post('/push/subscribe', { endpoint: subJSON.endpoint, keys: subJSON.keys });
        setSubscribed(true);
        toast.success('Notifications push activees !');
      } catch {
        toast.error('Erreur activation');
      }
    }
    setLoading(false);
  };

  if (!('PushManager' in window)) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggle}
      disabled={loading}
      className={subscribed ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-slate-300'}
      data-testid="push-toggle-btn"
    >
      {subscribed ? (
        <><Bell className="h-4 w-4 mr-2 text-green-600" />Notifications push actives</>
      ) : (
        <><BellOff className="h-4 w-4 mr-2" />Activer les notifications push</>
      )}
    </Button>
  );
}

function urlBase64ToUint8ArrayForToggle(base64String) {
  return urlBase64ToUint8Array(base64String);
}
